import React from 'react';
import { Gift, CreditCard, AlertCircle, Key, ShieldOff, Users, Bell } from 'lucide-react';
import { useNotifications, useMarkNotificationRead, useMarkAllNotificationsRead } from '@/hooks/useApi';
import { formatDateTime } from '@/lib/utils';
import Button from '@/components/ui/Button';
import { PageHeader } from '@/components/ui/PageHeader';
import { Panel } from '@/components/ui/Panel';
import { EmptyState } from '@/components/ui/EmptyState';
import type { NotificationType } from '@/types';

const TYPE_ICONS: Record<NotificationType, React.ReactNode> = {
  coupon_redeemed: <Gift className="h-4 w-4" />,
  payment_succeeded: <CreditCard className="h-4 w-4" />,
  payment_failed: <AlertCircle className="h-4 w-4" />,
  token_created: <Key className="h-4 w-4" />,
  token_revoked: <ShieldOff className="h-4 w-4" />,
  token_rotated: <Key className="h-4 w-4" />,
  role_changed: <Users className="h-4 w-4" />,
};

const NotificationsPage: React.FC = () => {
  const { data, isLoading } = useNotifications();
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  const notifications = data?.notifications ?? [];
  const hasUnread = notifications.some((n) => n.read_at === null);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Notifications"
        description="Usage, billing and account activity."
        actions={
          <Button
            variant="outline"
            size="sm"
            disabled={!hasUnread}
            loading={markAllRead.isLoading}
            onClick={() => markAllRead.mutate()}
          >
            Mark all as read
          </Button>
        }
      />

      <Panel bodyClassName="p-0">
        {isLoading ? (
          <ul className="divide-y divide-border" aria-hidden="true">
            {[0, 1, 2, 3].map((i) => (
              <li key={i} className="flex items-start gap-3 px-5 py-4">
                <div className="skeleton h-9 w-9 shrink-0 rounded-full" />
                <div className="flex-1 space-y-2"><div className="skeleton h-4 w-48" /><div className="skeleton h-3 w-72 max-w-full" /></div>
              </li>
            ))}
          </ul>
        ) : notifications.length === 0 ? (
          <EmptyState icon={<Bell />} title="No notifications yet." description="Payments, coupons and key changes show up here." />
        ) : (
          <ul className="divide-y divide-border">
            {notifications.map((n) => {
              const unread = n.read_at === null;
              return (
                <li key={n.id}>
                  <button
                    onClick={() => unread && markRead.mutate(n.id)}
                    className={`flex w-full items-start gap-3 px-5 py-4 text-left transition-colors first:rounded-t-2xl hover:bg-surface-raised/60 ${unread ? 'bg-accent/[0.04]' : ''}`}
                  >
                    <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${unread ? 'bg-accent/10 text-accent-text' : 'bg-surface-raised text-muted-foreground'}`}>
                      {TYPE_ICONS[n.type]}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className={`text-sm text-foreground ${unread ? 'font-medium' : ''}`}>{n.title}</p>
                        {unread && <span className="h-1.5 w-1.5 rounded-full bg-accent" aria-label="Unread" />}
                      </div>
                      <p className="mt-0.5 text-sm text-muted-foreground">{n.message}</p>
                      <p className="mt-1 font-mono text-xs tabular-nums text-muted-foreground sm:hidden">{formatDateTime(new Date(n.created_at * 1000))}</p>
                    </div>
                    <p className="hidden shrink-0 pt-0.5 font-mono text-xs tabular-nums text-muted-foreground sm:block">
                      {formatDateTime(new Date(n.created_at * 1000))}
                    </p>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </Panel>
    </div>
  );
};

export default NotificationsPage;
